import React, { useEffect, useState } from 'react';
import { Avatar, IconButton, Menu, Box, Tooltip, CircularProgress } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useProbeProfilePhotoQuery } from '../../features/media/api/mediaProbeApi';
import { buildProfilePhotoThumbnailUrl } from '../../features/profile-photo/profilePhotoThumbnailUrl';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import type { UserProfile } from '../../services/userService';
import { UserMenuActions } from './UserMenuActions';

interface UserMenuProps {
  userProfile: UserProfile | null;
  loading: boolean;
}

export const UserMenu: React.FC<UserMenuProps> = ({ userProfile, loading }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const profilePhotoQuery = useProbeProfilePhotoQuery(userProfile?.Id ?? 0, {
    skip: !userProfile?.Id,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [photoFailed, setPhotoFailed] = useState(false);
  const open = Boolean(anchorEl);
  const originalUrl = profilePhotoQuery.data?.resolvedUrl ?? null;
  const photoUrl = buildProfilePhotoThumbnailUrl(
    userProfile?.Id,
    Boolean(originalUrl),
    originalUrl
  );
  const authenticatedPhotoUrl = useAuthenticatedImage(photoUrl);

  useEffect(() => {
    setPhotoFailed(false);
  }, [photoUrl]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getProfileRoute = (profileUrl: string) => {
    const normalizePath = (path: string) => {
      if (!path || path === '/') return '/';
      return path.replace(/\/+$/, '');
    };
    try {
      const parsedUrl = new URL(profileUrl, window.location.origin);
      const normalizedPath = normalizePath(parsedUrl.pathname);
      return `${normalizedPath}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return normalizePath(profileUrl);
    }
  };

  const handleSettings = () => {
    handleClose();
    if (userProfile?.ProfileUrl) {
      navigate({ to: getProfileRoute(userProfile.ProfileUrl) });
    }
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
    } finally {
      navigate({ to: '/login' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
        <CircularProgress size={24} color="inherit" />
      </Box>
    );
  }

  // If no user profile loaded but we are authenticated, still render something
  const initials = userProfile?.Initials || '?';

  return (
    <>
      <Tooltip title="My profile">
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar
            src={photoFailed ? undefined : (authenticatedPhotoUrl ?? undefined)}
            imgProps={{ onError: () => setPhotoFailed(true) }}
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
              color: 'white',
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <UserMenuActions
          userProfile={userProfile}
          onSettings={handleSettings}
          onLogout={handleLogout}
        />
      </Menu>
    </>
  );
};
